/**
 * Extract a short, user-safe message from a failed fetch Response.
 * Never returns stack traces or raw JSON bodies.
 */
export async function getApiErrorMessage(
  response: Response,
  fallback = 'Something went wrong. Please try again.'
): Promise<string> {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const body = await response.json();
      return messageFromApiBody(body, fallback);
    } else {
      // Consume body so the connection can close; do not expose it.
      await response.text();
    }
  } catch {
    // ignore parse errors
  }
  return fallback;
}

/** Sync helper when response JSON was already parsed. */
export function messageFromApiBody(
  body: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  if (body && typeof body === 'object') {
    const msg = (body as { message?: unknown; error?: unknown }).message
      || (body as { error?: unknown }).error;
    if (typeof msg === 'string' && msg.trim()) {
      return sanitizeUserMessage(msg);
    }
  }
  return fallback;
}

/** Strip accidental JSON / stack leakage from strings that may already be polluted. */
export function sanitizeUserMessage(message: string, maxLen = 200): string {
  let msg = String(message || '').trim();
  // If someone concatenated a JSON body onto the message, keep only the prefix.
  const jsonStart = msg.search(/\{[\s]*"(success|message|error|stack)"/);
  if (jsonStart > 0) {
    msg = msg.slice(0, jsonStart).trim().replace(/[:\s]+$/, '');
  }
  if (
    msg.includes('"stack"') ||
    msg.includes('\n    at ') ||
    /Unknown column/i.test(msg) ||
    /ER_BAD_FIELD_ERROR/i.test(msg) ||
    /SequelizeDatabaseError/i.test(msg) ||
    /sqlMessage/i.test(msg)
  ) {
    return 'Something went wrong. Please try again.';
  }
  if (msg.length > maxLen) {
    return `${msg.slice(0, maxLen).trim()}…`;
  }
  return msg || 'Something went wrong. Please try again.';
}

export function toUserErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  if (error instanceof Error && error.message) {
    return sanitizeUserMessage(error.message, 160) || fallback;
  }
  if (typeof error === 'string' && error.trim()) {
    return sanitizeUserMessage(error, 160) || fallback;
  }
  return fallback;
}
